/**
 * Domínio: o verbo de cada linha da trilha — GUPM-335.
 *
 * `audit_changes()` grava `action = tg_op`: toda transição de TARP (confirmar,
 * concluir, assumir, dar baixa, descartar) chega como "UPDATE". Com o estado
 * anterior (`payload_before`) dá para dizer QUAL transição foi — é isso que
 * `descreverAcao` faz. Linha anterior ao GUPM-335 não tem estado anterior; aí o
 * verbo NÃO é inventado.
 *
 * TS puro: sem React, sem Supabase, sem DOM.
 */

import type { AuditEntry } from './index';

/** Ações semânticas (não-CRUD) com nome próprio. */
const ACAO_SEMANTICA: Record<string, string> = {
  // Acesso (GUPM-335)
  login_iniciado: 'Tentativa de login iniciada',
  login_sucesso: 'Login',
  login_sem_acesso: 'Login sem acesso ao app',
  login_falha_provedor: 'Falha de login — Microsoft/Supabase',
  // C-81: não é mais gravada (só o login dev a emitia); o nome fica para as
  // linhas já existentes nos bancos locais.
  login_falha_credenciais: 'Falha de login — senha incorreta',
  limite_eventos_anonimos: 'Limite de tentativas anônimas atingido',
  logout: 'Saída (clique em Sair)',
  sessao_encerrada: 'Sessão encerrada',
  acesso_negado: 'Acesso negado a uma tela',
  exportacao_dados: 'Exportação de dados',
  // TOTP (verify-totp / provision-totp)
  shift_totp_success: 'Código TOTP validado',
  shift_totp_failed: 'Código TOTP incorreto',
  shift_totp_failed_during_lockout: 'Código TOTP durante bloqueio',
  shift_totp_lockout: 'TOTP bloqueado por tentativas',
  shift_totp_sem_cadastro: 'TOTP pedido a quem não tem autenticador',
  shift_totp_enrollment_verified: 'Autenticador validado no cadastro',
  shift_totp_enrollment_failed: 'Código incorreto no cadastro do autenticador',
  totp_cadastro_iniciado: 'Cadastro de autenticador iniciado',
  // Instrumento — gravadas à mão por migrations de correção (existem em produção)
  instrument_fora_de_servico_declarado: 'Instrumento declarado fora de serviço',
  instrument_soft_delete: 'Instrumento retirado do cadastro',
  instrument_dado_em_revisao: 'Dado do instrumento marcado em revisão',
  instrument_coord_em_revisao: 'Coordenada do instrumento marcada em revisão',
  instrument_coord_restaurada: 'Coordenada do instrumento restaurada',
  // Máquina
  instrument_sem_sinal: 'Instrumento sem sinal',
  instrument_sinal_restabelecido: 'Sinal do instrumento restabelecido',
  feed_gpx_defasado: 'Feed GPX defasado',
  export_sharepoint_defasado: 'Export ao SharePoint atrasado',
  BULK_DELETE: 'Exclusão em massa',
  BULK_UPDATE: 'Edição em massa',
};

const CRUD: Record<string, string> = {
  INSERT: 'Criação',
  UPDATE: 'Edição',
  DELETE: 'Exclusão',
};

/** Transição de status da TARP → verbo. Chave: `DE>PARA`. */
const TRANSICAO_TARP: Record<string, string> = {
  'EM_ABERTURA>ABERTA': 'Abertura da TARP confirmada',
  'ABERTA>CONCLUIDA_AGUARDANDO': 'TARP concluída — aguarda o RT',
  'EM_ABERTURA>CONCLUIDA_AGUARDANDO': 'TARP concluída — aguarda o RT',
  'CONCLUIDA_AGUARDANDO>EM_ANALISE_RT': 'TARP assumida pelo RT',
  'CONCLUIDA_AGUARDANDO>ENCERRADA': 'Baixa da TARP',
  'EM_ANALISE_RT>ENCERRADA': 'Baixa da TARP',
};

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function descreverTarp(entry: AuditEntry): string {
  const depois = obj(entry.payload);
  const antes = obj(entry.payloadBefore);
  const status = typeof depois?.status === 'string' ? depois.status : null;

  if (entry.action === 'INSERT') {
    return status === 'EM_ABERTURA' ? 'Rascunho de TARP criado' : 'TARP aberta';
  }
  if (entry.action !== 'UPDATE') return CRUD[entry.action] ?? entry.action;

  if (!antes) return status ? `TARP atualizada (${status})` : 'TARP atualizada';

  if (!antes.deleted_at && depois?.deleted_at) return 'Rascunho de TARP descartado';
  const de = typeof antes.status === 'string' ? antes.status : null;
  if (de && status && de !== status) {
    return TRANSICAO_TARP[`${de}>${status}`] ?? `TARP: ${de} → ${status}`;
  }
  return status === 'EM_ABERTURA' ? 'Rascunho de TARP editado' : 'TARP editada';
}

/** Ações com rótulo conhecido (CRUD + semânticas) — base do filtro de Ação. */
export function acoesConhecidas(): string[] {
  return [...Object.keys(CRUD), ...Object.keys(ACAO_SEMANTICA)];
}

/** Rótulo de uma ação sem olhar a linha (usado no filtro). Desconhecida → nome cru. */
export function rotuloDaAcao(action: string): string {
  return ACAO_SEMANTICA[action] ?? CRUD[action] ?? action;
}

/** Verbo legível em pt-BR de uma linha da trilha. Desconhecida → nome cru. */
export function descreverAcao(entry: AuditEntry): string {
  const semantica = ACAO_SEMANTICA[entry.action];
  if (semantica) return semantica;
  if (entry.entity === 'tarp_records') return descreverTarp(entry);
  if (entry.entity === 'readings' && entry.action === 'UPDATE') {
    const depois = obj(obj(entry.payload)?.after);
    const antes = obj(obj(entry.payload)?.before);
    if (depois?.deleted_at && !antes?.deleted_at) return 'Leitura removida';
  }
  return CRUD[entry.action] ?? entry.action;
}

/** Um campo alterado, já formatado para exibição. */
export interface CampoAlterado {
  campo: string;
  antes: string;
  depois: string;
}

/** Colunas cujo VALOR nunca aparece — só o fato de terem mudado. */
const CAMPOS_OCULTOS = new Set(['totp_secret_base32']);
const OCULTO = '(oculto)';

function formatar(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Antes → depois campo a campo, na ordem de `changedFields`.
 *
 * `readings` guarda `{before, after}` no próprio payload (GUPM-253); as demais
 * guardam o antes em `payloadBefore`. `updated_at` sai: é o carimbo, não o fato.
 * Sem estado anterior → lista vazia (INSERT/DELETE: a própria linha é o registro).
 */
export function camposAlterados(entry: AuditEntry): CampoAlterado[] {
  const payload = obj(entry.payload);
  const embutido = entry.entity === 'readings' && payload && 'before' in payload;
  const antes = embutido ? obj(payload.before) : obj(entry.payloadBefore);
  const depois = embutido ? obj(payload.after) : payload;
  if (!antes || !depois) return [];

  const campos =
    entry.changedFields ??
    [...new Set([...Object.keys(antes), ...Object.keys(depois)])].filter(
      (k) => formatar(antes[k]) !== formatar(depois[k]),
    );

  return campos
    .filter((campo) => campo !== 'updated_at')
    .map((campo) =>
      CAMPOS_OCULTOS.has(campo)
        ? { campo, antes: OCULTO, depois: OCULTO }
        : { campo, antes: formatar(antes[campo]), depois: formatar(depois[campo]) },
    );
}
